import {sortBy} from 'ramda';

import {omneticApi as api, invalidateOnSuccess} from '@dms/api/base';

import {
  CreatePurchasePriceRuleApiArg,
  CreatePurchasePriceRuleApiResponse,
  CreatePurchasePriceRuleApiResponseSchema,
  DeletePurchasePriceRuleApiArg,
  DeletePurchasePriceRuleApiResponse,
  DeletePurchasePriceRuleApiResponseSchema,
  GetPurchasePriceRuleApiArg,
  GetPurchasePriceRuleApiResponse,
  GetPurchasePriceRuleApiResponseSchema,
  ListPurchasePriceRulesApiResponse,
  ListPurchasePriceRulesApiResponseSchema,
  ReorderPurchasePriceRulesApiArg,
  ReorderPurchasePriceRulesApiResponse,
  ReorderPurchasePriceRulesApiResponseSchema,
  UpdatePurchasePriceRuleApiArg,
  UpdatePurchasePriceRuleApiResponse,
  UpdatePurchasePriceRuleApiResponseSchema,
} from './types';

const PURCHASE_PRICE_RULE_URL = '/dms/v1/settings/sales/purchase-price-rule';

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPurchasePriceRules: build.query<ListPurchasePriceRulesApiResponse, void>({
      query: () => ({
        url: PURCHASE_PRICE_RULE_URL,
      }),
      extraOptions: {
        responseSchema: ListPurchasePriceRulesApiResponseSchema,
      },
      providesTags: [{type: 'PurchasePriceRule', id: 'LIST'}],
    }),
    getPurchasePriceRule: build.query<GetPurchasePriceRuleApiResponse, GetPurchasePriceRuleApiArg>({
      query: (queryArg) => ({
        url: `${PURCHASE_PRICE_RULE_URL}/${queryArg.id}`,
      }),
      extraOptions: {
        responseSchema: GetPurchasePriceRuleApiResponseSchema,
      },
      providesTags: (result, error, queryArg) => [{type: 'PurchasePriceRule', id: queryArg.id}],
    }),
    createPurchasePriceRule: build.mutation<
      CreatePurchasePriceRuleApiResponse,
      CreatePurchasePriceRuleApiArg
    >({
      query: (queryArg) => ({
        url: PURCHASE_PRICE_RULE_URL,
        method: 'POST',
        body: queryArg.purchasePriceRuleRequestBody,
      }),
      extraOptions: {
        responseSchema: CreatePurchasePriceRuleApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess([{type: 'PurchasePriceRule', id: 'LIST'}]),
    }),
    updatePurchasePriceRule: build.mutation<
      UpdatePurchasePriceRuleApiResponse,
      UpdatePurchasePriceRuleApiArg
    >({
      query: (queryArg) => ({
        url: `${PURCHASE_PRICE_RULE_URL}/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.purchasePriceRuleRequestBody,
      }),
      extraOptions: {
        responseSchema: UpdatePurchasePriceRuleApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: UpdatePurchasePriceRuleApiArg) => [
        {type: 'PurchasePriceRule', id: queryArg.id},
        {type: 'PurchasePriceRule', id: 'LIST'},
      ]),
    }),
    deletePurchasePriceRule: build.mutation<
      DeletePurchasePriceRuleApiResponse,
      DeletePurchasePriceRuleApiArg
    >({
      query: (queryArg) => ({
        url: `${PURCHASE_PRICE_RULE_URL}/${queryArg.id}`,
        method: 'DELETE',
      }),
      extraOptions: {
        responseSchema: DeletePurchasePriceRuleApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess((result, queryArg: DeletePurchasePriceRuleApiArg) => [
        {type: 'PurchasePriceRule', id: queryArg.id},
        {type: 'PurchasePriceRule', id: 'LIST'},
      ]),
    }),
    reorderPurchasePriceRules: build.mutation<
      ReorderPurchasePriceRulesApiResponse,
      ReorderPurchasePriceRulesApiArg
    >({
      query: (queryArg) => ({
        url: `${PURCHASE_PRICE_RULE_URL}/reorder`,
        method: 'PUT',
        body: queryArg.reorderPurchasePriceRulesRequestBody,
      }),
      onQueryStarted(queryArg, {dispatch, queryFulfilled}) {
        const positionById = new Map(
          queryArg.reorderPurchasePriceRulesRequestBody.items.map((item) => [
            item.id,
            item.position,
          ])
        );

        const patchResult = dispatch(
          injectedRtkApi.util.updateQueryData('listPurchasePriceRules', undefined, (draft) => {
            const reorderedRules = sortBy(
              (rule) => rule.position,
              draft
                .filter((rule) => !rule.system)
                .map((rule) => ({...rule, position: positionById.get(rule.id) ?? rule.position}))
            );

            // The system rule is never sent, it always stays last.
            return [...reorderedRules, ...draft.filter((rule) => rule.system)];
          })
        );

        // A failed save returns the list to the last saved order.
        queryFulfilled.catch(() => patchResult.undo());
      },
      extraOptions: {
        responseSchema: ReorderPurchasePriceRulesApiResponseSchema,
      },
      // Refetching refreshes `shadowedByRuleId`, which depends on the new order.
      invalidatesTags: invalidateOnSuccess([{type: 'PurchasePriceRule', id: 'LIST'}]),
    }),
  }),
  overrideExisting: false,
});

export const {
  useListPurchasePriceRulesQuery,
  useGetPurchasePriceRuleQuery,
  useCreatePurchasePriceRuleMutation,
  useUpdatePurchasePriceRuleMutation,
  useDeletePurchasePriceRuleMutation,
  useReorderPurchasePriceRulesMutation,
} = injectedRtkApi;
